import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Building2, ShieldCheck, Ban } from "lucide-react";

export default function AdminCommerces() {
  const { user, role, loading } = useAuth();
  const [items, setItems] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (role !== "client" || !user?.isAdmin) { nav("/"); return; }
    load();
  }, [loading, role, user, nav]); // eslint-disable-line

  const load = async () => {
    try {
      const { data } = await api.get("/admin/commerces");
      setItems(data);
    } catch {
      setItems([]);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/commerces/${c.id}`, { isActive: !c.isActive });
      toast.success(c.isActive ? "Établissement suspendu" : "Établissement réactivé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (items === null) return <div className="container mx-auto py-16 text-center text-subtle">Chargement…</div>;

  return (
    <div className="container mx-auto px-4 md:px-8 py-10 md:py-14 max-w-6xl">
      <p className="overline mb-2">Admin · Établissements</p>
      <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-ink">Modération des établissements</h1>
      <p className="mt-2 text-subtle">{items.length} établissement{items.length > 1 ? "s" : ""} inscrit{items.length > 1 ? "s" : ""}.</p>

      <div className="mt-8">
        {items.length === 0 ? (
          <div className="border border-dashed border-border p-12 text-center">
            <Building2 className="mx-auto text-subtle" size={36} />
            <p className="mt-4 text-ink font-medium">Aucun établissement inscrit pour le moment</p>
          </div>
        ) : (
          <div className="border border-border divide-y divide-border">
            {items.map((c) => (
              <div key={c.id} className="flex items-center gap-4 p-5" data-testid={`admin-commerce-row-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display font-bold text-ink truncate">{c.name}</p>
                    <span className="text-xs text-subtle">· {c.type}</span>
                    {c.emailVerified && <span className="inline-flex items-center gap-1 text-xs text-[#0A7F2E] font-bold"><ShieldCheck size={12} /> Vérifié</span>}
                    {!c.isActive && <span className="inline-flex items-center gap-1 text-xs text-destructive font-bold uppercase"><Ban size={12} /> Suspendu</span>}
                  </div>
                  <p className="text-xs text-subtle mt-1 truncate">
                    {c.address}, {c.postalCode} {c.city} · {c.email} · {c.phone}
                  </p>
                  <p className="text-xs text-subtle mt-1">
                    {c.siret && <span>SIRET {c.siret} · </span>}
                    {c.rna && <span>RNA {c.rna} · </span>}
                    {c.isAssociation && <span>Association · </span>}
                    Servis: {c.totalServed} · Abonnement: <strong className="text-ink">{c.subscriptionStatus}</strong>
                    {" · "}Créé le {new Date(c.createdAt).toLocaleDateString("fr-FR")}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-subtle font-medium">{c.isActive ? "Actif" : "Suspendu"}</span>
                  <Switch checked={c.isActive} onCheckedChange={() => toggleActive(c)} data-testid={`toggle-active-${c.id}`} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
